import React, { useState } from "react";
import { useList, useCreate } from "@refinedev/core";
import Markdown from "react-markdown";
import { VaultSidebar, VaultDocItem } from "@/components/digibox/VaultSidebar";
import { VaultActionBar } from "@/components/digibox/VaultActionBar";
import { VaultCommentPanel } from "@/components/digibox/VaultCommentPanel";
import { VaultDocumentPanel } from "@/components/digibox/VaultDocumentPanel";
import { usePortalContact } from "@/hooks/usePortalContact";

export const ContentList: React.FC = () => {
  const { contactId } = usePortalContact();
  const [selectedId, setSelectedId] = useState<string>();
  const [actionLoading, setActionLoading] = useState(false);
  const [justCompleted, setJustCompleted] = useState<Record<string, boolean>>({});
  const [commentsPanelOpen, setCommentsPanelOpen] = useState(false);

  // coreBOS portal session handles data scoping automatically
  const { data: contentData, isLoading } = useList({
    resource: "content",
    pagination: { pageSize: 50 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });

  const { data: acceptanceData, refetch: refetchAcceptance } = useList({
    resource: "acceptance",
    pagination: { pageSize: 100 },
  });

  const { mutateAsync: createAcceptance } = useCreate();

  const contents = (contentData?.data || []) as any[];
  const acceptances = (acceptanceData?.data || []) as any[];

  // Check if a content item has been read/accepted
  const getContentAcceptance = (contentId: string) =>
    acceptances.find((a: any) => a.acceptancetocontent === contentId);

  const documents: VaultDocItem[] = contents.map((c: any) => {
    const acc = getContentAcceptance(c.id);
    const isRead = justCompleted[c.id] || !!acc;
    return {
      id: c.id,
      category: c.page || c.position || "Content",
      title: c.title || "Untitled",
      date: c.createdtime
        ? new Date(c.createdtime).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        : "—",
      status: isRead ? "read" : "action",
    };
  });

  const selectedContent = contents.find((c: any) => c.id === selectedId);
  const selectedAcceptance = selectedId ? getContentAcceptance(selectedId) : null;
  const selectedIsCompleted =
    (selectedId && justCompleted[selectedId]) || !!selectedAcceptance;

  const handleMarkRead = async () => {
    if (!selectedContent) return;
    setActionLoading(true);
    try {
      await createAcceptance({
        resource: "acceptance",
        values: {
          acctitle: `Read: ${selectedContent.title || selectedId}`,
          acceptance_status: "Read",
          acclastdate: new Date().toISOString().slice(0, 10),
          acceptancetocontent: selectedId,
          ...(contactId ? { pacclinktocontact: contactId } : {}),
          accmethod: "Portal",
          assigned_user_id: "19x1",
          description: `Content "${selectedContent.title}" marked as read via DigiBox`,
        },
      });
      setJustCompleted((prev) => ({ ...prev, [selectedId!]: true }));
      refetchAcceptance();
    } catch {
      // Error handled by refine notification provider
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <VaultSidebar
        documents={documents}
        activeId={selectedId}
        onSelect={setSelectedId}
        loading={isLoading}
      />

      <main className="vault-panel vault-main-content">
        {!selectedContent ? (
          <div className="vault-hub-content">
            <div className="vault-hub-hero">
              <h1 className="vault-heading-display">
                CONTENUTI
                <br />
                DA VISIONARE
              </h1>
            </div>
            <div style={{ marginTop: "var(--vault-spacing-md)" }}>
              <p
                className="vault-text-body-editorial"
                style={{ color: "var(--vault-fg-muted)" }}
              >
                Seleziona un contenuto dalla barra laterale per visualizzarne
                i dettagli e le informazioni associate.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="vault-article-scroll-area">
              <header className="vault-article-header">
                <div className="vault-article-title-area">
                  <h1 className="vault-heading-display">
                    {(selectedContent.title || "").toUpperCase()}
                  </h1>
                </div>
                <div className="vault-article-meta-row">
                  <div className="vault-meta-block">
                    <span className="vault-label-tiny">Ref</span>
                    <span className="vault-text-small">
                      {selectedContent.cbcontent_no || selectedContent.id}
                    </span>
                  </div>
                  {selectedContent.page && (
                    <div className="vault-meta-block">
                      <span className="vault-label-tiny">Pagina</span>
                      <span className="vault-text-small">
                        {selectedContent.page}
                      </span>
                    </div>
                  )}
                  <div className="vault-meta-block vault-meta-block-right">
                    <span className="vault-label-tiny">Data</span>
                    <span className="vault-text-small">
                      {selectedContent.createdtime
                        ? new Date(
                            selectedContent.createdtime
                          ).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                    </span>
                  </div>
                </div>
              </header>

              <div className="vault-article-body" style={{ gridTemplateColumns: "1fr" }}>
                <div className="vault-article-main-text vault-text-body-editorial">
                  {selectedContent.description && (
                    <p className="vault-text-muted" style={{ marginBottom: "var(--vault-spacing-sm)" }}>
                      {selectedContent.description}
                    </p>
                  )}
                  {selectedContent.content ? (
                    <Markdown>{selectedContent.content}</Markdown>
                  ) : (
                    <p className="vault-text-muted">
                      Nessun contenuto disponibile.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <VaultDocumentPanel recordId={selectedId!} module="cbContent" allowUpload={selectedContent?.allow_upload === "1" || selectedContent?.allow_upload === true} />

            {selectedIsCompleted ? (
              <VaultActionBar
                statement=""
                buttonLabel=""
                onAction={async () => {}}
                completed
                completedLabel="Contenuto letto"
                onToggleComments={() => setCommentsPanelOpen((v) => !v)}
                commentsOpen={commentsPanelOpen}
              />
            ) : (
              <VaultActionBar
                statement="Leggi questo contenuto e confermane la presa visione."
                buttonLabel="Mark as Read"
                onAction={handleMarkRead}
                loading={actionLoading}
                onToggleComments={() => setCommentsPanelOpen((v) => !v)}
                commentsOpen={commentsPanelOpen}
              />
            )}
          </>
        )}
      </main>

      {selectedId && (
        <VaultCommentPanel
          relatedTo={selectedId}
          open={commentsPanelOpen}
          onClose={() => setCommentsPanelOpen(false)}
        />
      )}
    </>
  );
};
